import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box, Collapse, useTheme} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import dayjs from 'dayjs'
import AppDialog from 'src/components-new/feedback/AppDialog'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import Button from 'src/components-new/primitives/Button'
import {STATUS, statesOptions} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import type {FilterItem, FilterResult} from './Filters'

interface PatientLookupDialogProps {
  onClose: () => void
  onRowClick: (row: Record<string, any>) => void
}

const DEFAULT_PAGINATION: PaginationState = {page: 0, rowsPerPage: 10}

// A rangePicker field stores its two bounds as ISO strings (or null), same shape
// DatePickerNew already speaks — no need for the legacy Filters.tsx's Dayjs objects.
type RangeValue = [string | null, string | null]

function buildDefaultValues(config: FilterItem[]): Record<string, any> {
  const defaults: Record<string, any> = {}
  config.forEach(({id, type}) => {
    defaults[id] = type === 'checkbox' || type === 'multiSelect' ? [] : ''
  })
  return defaults
}

function buildDefaultRanges(config: FilterItem[]): Record<string, RangeValue> {
  const ranges: Record<string, RangeValue> = {}
  config.forEach(({id, type}) => {
    if (type === 'rangePicker') ranges[id] = [null, null]
  })
  return ranges
}

// Mirrors the {property, type, value} shape apiClient.postPatientsSearch/Count send to
// psp-backend/patients/search — same contract components-new/data/Filters.tsx's
// buildPostBody already produces for every other filter in this app.
function buildFilterResults(
  config: FilterItem[],
  values: Record<string, any>,
  ranges: Record<string, RangeValue>,
): FilterResult[] {
  return config
    .map((item): FilterResult | null => {
      if (item.type === 'rangePicker') {
        const [from, to] = ranges[item.id] ?? [null, null]
        if (!from && !to) return null
        return {
          property: item.id,
          type: item.type,
          // endOf('day') on `to` so the selected day is included, matching the legacy
          // Filters.tsx's own rangePicker submit semantics.
          value: [from, to ? dayjs(to).endOf('day').toISOString() : null],
        }
      }
      const raw = values[item.id]
      if (Array.isArray(raw)) {
        if (raw.length === 0) return null
        return {property: item.id, type: item.type, value: raw}
      }
      if (!raw) return null
      return {property: item.id, type: item.type ?? 'text', value: raw}
    })
    .filter((item): item is FilterResult => item !== null)
}

/**
 * New-style, PSP-config-driven equivalent of the legacy
 * src/components/templates/PatientLookupDialog/PatientLookupDialog.tsx, used by
 * components-new/data/Filters.tsx's `patientLookup` field type. Keeps full parity with
 * every filter field `patientsConfigurations.filters` can configure (plain text, select,
 * date range, checkbox group) but built on AppDialog + DataTable, so it gets the
 * responsive bottom-sheet/mobile-card behavior the legacy dialog never had.
 */
export default function PatientLookupDialog({onClose, onRowClick}: PatientLookupDialogProps): JSX.Element {
  const theme = useTheme()
  // This dialog itself opens at theme.zIndex.modal + 2 (see the `sx` prop on AppDialog
  // below), to render above the Filters Drawer that hosts it. Any popup a field inside
  // this dialog opens (a Select's menu, in particular) needs to clear THAT, or it renders
  // behind this dialog's own Paper — technically present in the DOM, but invisible/inert.
  const fieldPopupZIndex = theme.zIndex.modal + 3
  const patientsConfigurations = useAppSelector(state => state.pspConfigReducer.data.patientsConfigurations)
  const filterConfig: FilterItem[] = patientsConfigurations?.filters ?? []
  const columnsConfig: {id: string; label: string}[] = patientsConfigurations?.columns ?? []
  // Filters only make sense as a way to narrow down what the table's columns show — with no
  // columns configured there's nothing to search into and no result to make sense of, so hide
  // the whole "Filtra" affordance rather than offering a filter panel over an empty table.
  const hasColumns = columnsConfig.length > 0

  // Collapsed by default: the fields sit in normal document flow (a Collapse, not a
  // popover/overlay), so showing them doesn't reintroduce the absolutely-positioned-panel
  // sizing bugs the legacy dialog had — but with up to 8 configurable fields, leaving them
  // open by default would still push the results table below the fold on first look.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [values, setValues] = useState<Record<string, any>>(() => buildDefaultValues(filterConfig))
  const [ranges, setRanges] = useState<Record<string, RangeValue>>(() => buildDefaultRanges(filterConfig))
  const [appliedFilters, setAppliedFilters] = useState<FilterResult[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [count, setCount] = useState(0)
  const [status, setStatus] = useState<string>()
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION)
  // Guards against out-of-order responses: the initial, unfiltered fetch this dialog fires on
  // mount can still be in flight when the user opens "Filtra" and searches — without this, a
  // slower unfiltered response landing after a faster filtered one silently overwrites the
  // filtered result with the full list, making the search appear to do nothing.
  const latestRequestIdRef = useRef(0)

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current
    const paginationFilters = {rowsPerPage: pagination.rowsPerPage, page: pagination.page, order: []}
    const fetchedData = await apiClient.postPatientsSearch(appliedFilters, setStatus, paginationFilters) ?? []
    const fetchedCount = await apiClient.postPatientsSearchCount(appliedFilters, setStatus)
    if (requestId !== latestRequestIdRef.current) return
    setData(fetchedData.map((patient: any) => ({
      ...patient,
      state: statesOptions.find(option => option.value === patient.state)?.label ?? patient.state,
    })))
    const n = Number(fetchedCount)
    if (!isNaN(n)) setCount(n)
  }, [appliedFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const setField = (id: string, value: any) => setValues(prev => ({...prev, [id]: value}))
  const setRange = (id: string, index: 0 | 1, value: string | null) => setRanges(prev => {
    const current = prev[id] ?? [null, null]
    const next: RangeValue = index === 0 ? [value, current[1]] : [current[0], value]
    return {...prev, [id]: next}
  })

  const handleSearch = () => {
    setAppliedFilters(buildFilterResults(filterConfig, values, ranges))
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleClear = () => {
    setValues(buildDefaultValues(filterConfig))
    setRanges(buildDefaultRanges(filterConfig))
    setAppliedFilters([])
    setPagination(prev => ({...prev, page: 0}))
  }

  const renderField = (item: FilterItem) => {
    switch (item.type) {
      case 'select':
        return (
          <SelectNew
            allowClear
            fullWidth
            label={item.label}
            menuZIndex={fieldPopupZIndex}
            onChange={(value) => setField(item.id, value)}
            options={item.options ?? []}
            value={values[item.id] ?? ''}
          />
        )
      case 'rangePicker': {
        const range = ranges[item.id] ?? [null, null]
        return (
          <Box sx={{display: 'flex', gap: 1}}>
            <DatePickerNew
              fullWidth
              label={`${item.label} da`}
              onChange={(value) => setRange(item.id, 0, value)}
              value={range[0]}
            />
            <DatePickerNew
              fullWidth
              label={`${item.label} a`}
              onChange={(value) => setRange(item.id, 1, value)}
              value={range[1]}
            />
          </Box>
        )
      }
      case 'checkbox':
        return (
          <CheckboxGroupNew
            label={item.label}
            onChange={(value) => setField(item.id, value)}
            options={item.options ?? []}
            value={values[item.id] ?? []}
          />
        )
      default:
        return (
          <TextFieldNew
            fullWidth
            label={item.label}
            onChange={(value) => setField(item.id, value)}
            value={values[item.id] ?? ''}
          />
        )
    }
  }

  const columns: ColumnDef<Record<string, any>>[] = columnsConfig.map(col => ({
    key: col.id,
    header: col.label,
    render: (row) => <>{row[col.id] ?? '—'}</>,
  }))

  return (
    <AppDialog
      actions={
        <>
          <Button onClick={handleClear} variant="secondary">Cancella</Button>
          <Button onClick={handleSearch} variant="primary">Cerca</Button>
        </>
      }
      maxWidth="lg"
      onClose={onClose}
      // Opened from inside components-new/data/Filters.tsx's Drawer (FILTERS_DRAWER_Z_INDEX
      // = modal + 1) — without this it renders behind that Drawer instead of above it.
      // Matches FILTERS_POPUP_Z_INDEX there exactly.
      sx={{zIndex: theme.zIndex.modal + 2}}
      open
      title="Lista pazienti"
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        {hasColumns && (
          <>
            <Button
              onClick={() => setFiltersOpen(prev => !prev)}
              startIcon={<FilterListIcon />}
              sx={{alignSelf: 'flex-start'}}
              variant="secondary"
            >
              {filtersOpen ? 'Nascondi filtri' : 'Filtra'}
            </Button>

            <Collapse in={filtersOpen}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
                  alignItems: 'end',
                  gap: 2,
                  pb: 1,
                }}
              >
                {filterConfig.map(item => (
                  <Box key={item.id}>{renderField(item)}</Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}

        <DataTable<Record<string, any>>
          columns={columns}
          emptyMessage="Nessun paziente trovato"
          keyField="_id"
          loading={status === STATUS.PENDING}
          onPaginationChange={setPagination}
          onRowClick={onRowClick}
          pagination={pagination}
          rows={data}
          totalElements={count}
        />
      </Box>
    </AppDialog>
  )
}
